import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { hasPermission } from "../utils/auth";

export default function ProposalReviewPage({ user }) {
  const [proposals, setProposals] = useState([]);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [insightModal, setInsightModal] = useState({ open: false, contract: null });
  const [insights, setInsights] = useState({ positives: [], negatives: [] });
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState("");
  const [accessModal, setAccessModal] = useState({
    open: false,
    contract: null,
    reason: "",
    status: "NOT_REQUESTED",
    decisionReason: "",
  });
  const [accessSubmitting, setAccessSubmitting] = useState(false);

  const canRead = hasPermission(user, "proposals:read");
  const canCreate = hasPermission(user, "proposals:create");
  const canDelete = hasPermission(user, "proposals:delete");
  const canProcess = hasPermission(user, "proposals:process");
  const canInsights = hasPermission(user, "proposals:insights");
  const canChat = hasPermission(user, "chat:use");
  const canRequestAccess = hasPermission(user, "access-requests:request");

  // In production the API is same-domain (baseURL is the relative "/api"),
  // so file links must stay relative too, resolving against whatever host
  // is actually serving the page. Only fall back to an absolute dev URL
  // when baseURL is itself an absolute URL (local dev, different origin).
  const rawApiBase = api.defaults.baseURL || "/api";
  const backendBaseUrl = /^https?:\/\//i.test(rawApiBase)
    ? rawApiBase.replace(/\/api$/, "")
    : "";

  const fetchProposals = async () => {
    if (!canRead) {
      setMessage("You do not have permission to view proposals.");
      return;
    }
    try {
      const res = await api.get("/proposals");
      setProposals(res.data);
    } catch (err) {
      console.error("Error fetching proposals:", err);
    }
  };

  useEffect(() => {
    fetchProposals();
  }, [canRead]);

  const filtered = proposals.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.title.toLowerCase().includes(q) ||
      (p.contract_type_name || "").toLowerCase().includes(q)
    );
  });

  const openInsights = async (proposal) => {
    if (!canInsights) return;

    const accessStatus = (proposal.access_status || "").toUpperCase();
    if (user?.role !== "CEO" && accessStatus !== "APPROVED") {
      setAccessModal({
        open: true,
        contract: proposal,
        reason: "",
        status: proposal.access_status || "NOT_REQUESTED",
        decisionReason: proposal.access_decision_reason || "",
      });
      return;
    }

    setInsightModal({ open: true, contract: proposal });
    setInsights({ positives: [], negatives: [] });
    setInsightsError("");
    setInsightsLoading(true);
    try {
      const res = await api.get(`/proposals/${proposal.id}/insights`);
      setInsights({
        positives: res.data.positives || [],
        negatives: res.data.negatives || [],
      });
    } catch (err) {
      if (err.response?.status === 403) {
        setInsightModal({ open: false, contract: null });
        setAccessModal({
          open: true,
          contract: proposal,
          reason: "",
          status: err.response?.data?.access_status || "NOT_REQUESTED",
          decisionReason: "",
        });
      } else {
        setInsightsError(err.response?.data?.error || "Failed to load insights.");
      }
    } finally {
      setInsightsLoading(false);
    }
  };

  const refreshInsights = async () => {
    if (!insightModal.contract || !canInsights) return;
    setInsightsLoading(true);
    setInsightsError("");
    try {
      const res = await api.post(`/proposals/${insightModal.contract.id}/insights/refresh`);
      setInsights({
        positives: res.data.positives || [],
        negatives: res.data.negatives || [],
      });
    } catch (err) {
      setInsightsError(err.response?.data?.error || "Failed to refresh insights.");
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!canDelete) return;
    const ok = window.confirm("Delete this proposal?");
    if (!ok) return;
    try {
      setDeletingId(id);
      await api.delete(`/proposals/${id}`);
      setProposals((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Delete proposal error:", err);
      setMessage(err.response?.data?.error || "Failed to delete proposal.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleProcess = async (id) => {
    if (!canProcess) return;
    try {
      await api.post(`/proposals/${id}/process`);
      await fetchProposals();
      setMessage("Processing started.");
    } catch (err) {
      console.error("Process error:", err);
      setMessage(err.response?.data?.error || "Failed to process proposal.");
    }
  };

  const submitAccessRequest = async () => {
    if (!accessModal.contract || !canRequestAccess) return;
    setAccessSubmitting(true);
    try {
      const res = await api.post(`/proposals/${accessModal.contract.id}/request-access`, {
        reason: accessModal.reason,
      });
      const nextStatus = res.data.status || "PENDING";
      setProposals((prev) =>
        prev.map((p) =>
          p.id === accessModal.contract.id ? { ...p, access_status: nextStatus } : p
        )
      );
      setAccessModal((prev) => ({ ...prev, open: false }));
    } catch (err) {
      console.error("Access request error:", err);
      setMessage(err.response?.data?.error || "Failed to request access.");
    } finally {
      setAccessSubmitting(false);
    }
  };

  const renderStatusBadge = (status) => {
    const base =
      "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border";
    if (status === "READY") {
      return <span className={`${base} bg-green-50 text-green-700 border-green-200`}>READY</span>;
    }
    if (status === "PROCESSING") {
      return (
        <span className={`${base} bg-amber-50 text-amber-700 border-amber-200`}>
          PROCESSING
        </span>
      );
    }
    if (status === "FAILED") {
      return <span className={`${base} bg-red-50 text-red-700 border-red-200`}>FAILED</span>;
    }
    return <span className={`${base} bg-slate-50 text-slate-600 border-slate-200`}>{status}</span>;
  };

  const accessBadge = (status) => {
    const normalized = (status || "NOT_REQUESTED").toUpperCase();
    if (normalized === "APPROVED") {
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    }
    if (normalized === "PENDING") {
      return "bg-amber-50 text-amber-700 border-amber-200";
    }
    if (normalized === "REJECTED") {
      return "bg-rose-50 text-rose-700 border-rose-200";
    }
    return "bg-slate-50 text-slate-600 border-slate-200";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-navy">Proposal Review</h1>
          <p className="text-sm text-slate-600 mt-1">
            Daily incoming proposals with AI insights (Deutsch).
          </p>
        </div>
        {canCreate && (
          <Link
            to="/proposals/new"
            className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold shadow-sm hover:bg-navy/90"
          >
            Upload Proposal
          </Link>
        )}
      </div>

      {message && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 text-sm text-amber-800">
          {message}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center justify-between gap-3 mb-4">
          <input
            type="text"
            placeholder="Search proposals..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-600">
                <th className="px-4 py-2 text-left font-semibold">Title</th>
                <th className="px-4 py-2 text-left font-semibold">Type</th>
                <th className="px-4 py-2 text-left font-semibold">Status</th>
                <th className="px-4 py-2 text-left font-semibold">Access</th>
                <th className="px-4 py-2 text-left font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-4 py-2">{p.title}</td>
                  <td className="px-4 py-2">{p.contract_type_name || "-"}</td>
                  <td className="px-4 py-2">{renderStatusBadge(p.status)}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${accessBadge(
                        p.access_status
                      )}`}
                    >
                      {p.access_status || "NOT_REQUESTED"}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-2">
                      {p.file_path ? (
                        <a
                          href={`${backendBaseUrl}/${p.file_path}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => {
                            e.stopPropagation();
                            const accessStatus = (p.access_status || "").toUpperCase();
                            if (user?.role !== "CEO" && accessStatus !== "APPROVED") {
                              e.preventDefault();
                              setAccessModal({
                                open: true,
                                contract: p,
                                reason: "",
                                status: p.access_status || "NOT_REQUESTED",
                                decisionReason: p.access_decision_reason || "",
                              });
                            }
                          }}
                          className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${
                            user?.role === "CEO" || p.access_status === "APPROVED"
                              ? "border-slate-300 text-slate-700 hover:bg-slate-50"
                              : "border-slate-200 text-slate-400 cursor-not-allowed"
                          }`}
                        >
                          View PDF
                        </a>
                      ) : (
                        <button
                          type="button"
                          disabled
                          onClick={(e) => e.stopPropagation()}
                          className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-500 bg-slate-100 cursor-not-allowed"
                        >
                          View PDF
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openInsights(p);
                        }}
                        disabled={!canInsights}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm ${
                          canInsights ? "bg-navy text-white hover:bg-navy/90" : "bg-slate-200 text-slate-500 cursor-not-allowed"
                        }`}
                      >
                        Insights
                      </button>

                      <Link
                        to={p.status === "READY" ? `/proposals/${p.id}/chat` : "#"}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm ${
                          p.status === "READY" && canChat
                            ? "bg-gold text-white hover:bg-gold/90"
                            : "bg-slate-200 text-slate-500 cursor-not-allowed"
                        }`}
                        onClick={(e) => {
                          if (p.status !== "READY" || !canChat) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                          e.stopPropagation();
                        }}
                      >
                        AI Chat
                      </Link>

                      {p.status !== "READY" && canProcess && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProcess(p.id);
                          }}
                          className="px-3 py-1.5 rounded-lg border border-amber-200 text-amber-700 text-xs font-semibold hover:bg-amber-50"
                        >
                          Process
                        </button>
                      )}

                      {canDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(p.id);
                          }}
                          disabled={deletingId === p.id}
                          className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 disabled:opacity-60"
                        >
                          {deletingId === p.id ? "Deleting…" : "Delete"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-4 py-6 text-center text-sm text-slate-500">
                    No proposals found. Upload a new proposal to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {insightModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-10">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl border border-slate-200">
            <div className="flex items-start justify-between p-5 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-semibold text-navy">Vorschlagsbewertung</h3>
                <p className="text-sm text-slate-600">
                  {insightModal.contract?.title}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setInsightModal({ open: false, contract: null })}
                className="text-slate-500 hover:text-slate-800 text-lg"
              >
                ×
              </button>
            </div>
            <div className="p-5 space-y-4">
              {insightsError && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-4 py-2 text-sm">
                  {insightsError}
                </div>
              )}
              {insightsLoading ? (
                <div className="text-sm text-slate-500">Loading insights…</div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-emerald-800">
                        Positive Punkte
                      </h4>
                      <span className="text-xs text-emerald-700">
                        {insights.positives.length} Punkte
                      </span>
                    </div>
                    <div className="max-h-72 overflow-auto pr-1">
                      <ul className="space-y-2 text-sm text-emerald-900">
                        {insights.positives.map((p, idx) => (
                          <li
                            key={`pos-${idx}`}
                            className="flex gap-2 rounded-lg bg-white/60 border border-emerald-100 px-3 py-2"
                          >
                            <span className="mt-0.5 text-emerald-700">+</span>
                            <span className="leading-snug">{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="border border-rose-200 bg-rose-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-rose-800">
                        Negative Punkte
                      </h4>
                      <span className="text-xs text-rose-700">
                        {insights.negatives.length} Punkte
                      </span>
                    </div>
                    <div className="max-h-72 overflow-auto pr-1">
                      <ul className="space-y-2 text-sm text-rose-900">
                        {insights.negatives.map((n, idx) => (
                          <li
                            key={`neg-${idx}`}
                            className="flex gap-2 rounded-lg bg-white/60 border border-rose-100 px-3 py-2"
                          >
                            <span className="mt-0.5 text-rose-700">−</span>
                            <span className="leading-snug">{n}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              {canInsights && (
                <button
                  type="button"
                  onClick={refreshInsights}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  disabled={insightsLoading}
                >
                  Refresh Insights
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {accessModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-10">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg border border-slate-200">
            <div className="flex items-start justify-between p-5 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-semibold text-navy">Access Required</h3>
                <p className="text-sm text-slate-600">
                  {accessModal.contract?.title}
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setAccessModal({ open: false, contract: null, reason: "", status: "NOT_REQUESTED" })
                }
                className="text-slate-500 hover:text-slate-800 text-lg"
              >
                ×
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-600">
                You need approval before viewing this proposal.
              </p>
              <textarea
                value={accessModal.reason}
                onChange={(e) => setAccessModal((prev) => ({ ...prev, reason: e.target.value }))}
                placeholder="Add a reason (optional)"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                rows={4}
              />
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAccessModal({ open: false, contract: null, reason: "", status: "NOT_REQUESTED" })}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitAccessRequest}
                  disabled={accessSubmitting || !canRequestAccess}
                  className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:bg-navy/90 disabled:opacity-60"
                >
                  {accessSubmitting ? "Submitting…" : "Request Access"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
